/** Types and calls for /api/admin/* — the staff console's data layer. */
import { request } from './api';

export interface AdminMe {
  admin: { id: string; name: string; email: string; initials: string };
}

export interface CompanySummary {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  userCount: number;
}

export interface CompanyDetail {
  id: string;
  name: string;
  status: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  billingAddressLine1: string | null;
  billingAddressLine2: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  phone: string | null;
  ein: string | null;
  createdAt: string;
  userCount: number;
  propertyCount: number;
  unitCount: number;
}

export interface CompanyUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
  lastActiveAt: string | null;
}

export interface CreatedWithInvite {
  setPasswordLink?: string | null;
  idpAccount?: 'created' | 'existing' | 'dev';
}

export interface StaffMember {
  id: string;
  email: string;
  name: string;
  status: string;
  createdAt: string;
  lastActiveAt: string | null;
}

export interface Assignment {
  userId: string;
  propertyId: string;
  isPrimary: boolean;
}

export const ROLE_LABEL: Record<string, string> = {
  compliance_lead: 'Compliance lead',
  regional_manager: 'Regional manager',
  site_coordinator: 'Site coordinator',
  owner_rep: 'Owner representative',
};

export const adminApi = {
  me: () => request<AdminMe>('/admin/me'),
  companies: () => request<{ companies: CompanySummary[] }>('/admin/companies'),
  company: (id: string) => request<{ company: CompanyDetail }>(`/admin/companies/${id}`),
  createCompany: (name: string) =>
    request<{ companyId: string }>('/admin/companies', { method: 'POST', body: { name } }),
  updateCompany: (id: string, fields: Record<string, string>) =>
    request(`/admin/companies/${id}`, { method: 'PATCH', body: fields }),
  companyUsers: (id: string) => request<{ users: CompanyUser[] }>(`/admin/companies/${id}/users`),
  createUser: (companyId: string, body: { name: string; email: string; role: string }) =>
    request<{ userId: string } & CreatedWithInvite>(`/admin/companies/${companyId}/users`, {
      method: 'POST',
      body,
    }),
  setUserStatus: (userId: string, status: string) =>
    request(`/admin/users/${userId}/status`, { method: 'POST', body: { status } }),
  properties: (companyId: string) =>
    request<{ properties: import('./api').Property[] }>(`/admin/companies/${companyId}/properties`),
  createProperty: (companyId: string, body: Record<string, unknown>) =>
    request<{ propertyId: string }>(`/admin/companies/${companyId}/properties`, { method: 'POST', body }),
  units: (propertyId: string) => request<{ units: import('./api').Unit[] }>(`/admin/properties/${propertyId}/units`),
  createUnit: (propertyId: string, body: Record<string, unknown>) =>
    request<{ unitId: string }>(`/admin/properties/${propertyId}/units`, { method: 'POST', body }),
  assignments: (companyId: string) =>
    request<{ assignments: Assignment[] }>(`/admin/companies/${companyId}/assignments`),
  setAssignment: (userId: string, propertyId: string, assigned: boolean) =>
    request(`/admin/users/${userId}/assignments`, { method: 'POST', body: { propertyId, assigned } }),
  staff: () => request<{ staff: StaffMember[]; self: string }>('/admin/staff'),
  createStaff: (body: { name: string; email: string }) =>
    request<{ id: string } & CreatedWithInvite>('/admin/staff', { method: 'POST', body }),
  setStaffStatus: (id: string, status: 'active' | 'disabled') =>
    request(`/admin/staff/${id}/status`, { method: 'POST', body: { status } }),
};

export function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const units: Array<[string, number]> = [
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [name, span] of units) {
    const n = Math.floor(seconds / span);
    if (n >= 1) return `${n} ${name}${n > 1 ? 's' : ''} ago`;
  }
  return 'Just now';
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
